<template>
  <a-modal
    :title="title"
    width="1020px"
    :open="open"
    :bodyStyle="{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', padding: '0 24px' }"
    :confirm-loading="loading"
    cancel-text="取消"
    ok-text="确定"
    centered
    destroy-on-close
    @cancel="handleCancel"
    @ok="handleOk"
  >
    <a-steps
      :current="currentStep"
      size="small"
      class="config-steps"
      @change="onStepChange"
    >
      <a-step title="基础配置" />
      <a-step title="展示配置" />
      <a-step title="列配置" />
      <a-step title="菜单" />
    </a-steps>

    <a-form
      ref="formRef"
      :model="model"
      layout="vertical"
    >
      <!-- ═══════════ Step 1 基础配置 ═══════════ -->
      <div
        v-show="currentStep === 0"
        class="step-pane"
      >
        <StepBasicConfig
          v-model:sql-error="sqlError"
          :model="model"
          :record="record"
          :form-type="form_type"
        />
      </div>

      <!-- ═══════════ Step 2 展示配置（view_meta 白名单透传给前端）
           ★ v29.18：order_json 迁到列配置（DbViewColumn 默认排序三字段）、
           max_page_size / pivot_table_max_records 机制废除，原「查询行为」步骤移除。
           ★ v29.45：原第 2 步「菜单与权限」挪到末位 → 本步上移到第 2 步 ═══════════ -->
      <!-- ★ v29.13：org_filter 配置已废除（用户定案：组织过滤只靠 SQL 里手写 {{org_id}}，
           引擎不做任何特殊处理），此处不再提供配置项 -->
      <div
        v-show="currentStep === 1"
        class="step-pane"
      >
        <StepDisplayConfig :model="model" />
      </div>

      <!-- ═══════════ Step 3 列配置（§3.1.4 字段配置子表：列探测刷新 → 人工覆盖）
           ★ v29.34：本步额外挂 `step-pane--col`，把「工具条 + 表格」改成 flex 纵向布局，
           让 s-table 吃掉剩余高度（详见样式区注释），父级不再出纵向滚动条。
           ★ v29.35：这层 div 是**高度链路的第 ① 环**，必须留着——StepColumnConfig 是
           多根（fragment）组件，它内部 `.col-toolbar` / `.col-table-wrapper` 会原样插进这里，
           成为本 div（flex column）的直接子元素；多包一层 DOM 就会让 wrapper 的 `flex: 1`
           失去参照。显隐也由本 div 的 v-show 负责（fragment 组件不继承 v-show / class）。
           ★ v29.45：本步由第 4 步上移到第 3 步（菜单步挪到末位）—— 显隐由 `colActive`
           统一表达，故这次换位只需改 `colActive` 的判据，不必动这里。 ═══════════ -->
      <div
        v-show="colActive"
        class="step-pane step-pane--col"
      >
        <StepColumnConfig
          ref="colStepRef"
          :active="colActive"
          :model="model"
          :record="record"
          :form-type="form_type"
          @probe-start="onProbeStart"
        />
      </div>

      <!-- ═══════════ Step 4 菜单（★ v29.45：原第 2 步「菜单与权限」挪到**末位**并改名「菜单」）
           裁掉两项：菜单索引（sort，改由后端在新建菜单时取 max+1）、可见角色（roles，改由
          「菜单的角色授权」承载）。只留父级路由 + 隶属项目，保存时由 save_view 同步到
          该视图对应的菜单行（不存在则新建）。 ═══════════ -->
      <div
        v-show="currentStep === 3"
        class="step-pane"
      >
        <StepMenu :model="model" />
      </div>
    </a-form>

    <template #footer>
      <a-button
        v-if="currentStep > 0"
        @click="currentStep -= 1"
      >
        上一步
      </a-button>
      <a-button
        v-if="currentStep < 3"
        type="primary"
        ghost
        @click="goNext"
      >
        下一步
      </a-button>
      <a-button @click="handleCancel">取消</a-button>
      <a-button
        type="primary"
        :loading="loading"
        :disabled="form_type !== 'edit' && currentStep < 3"
        @click="handleOk"
      >
        确定
      </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue';
import { computed, onMounted, ref } from 'vue';
import { event_obj } from 'liyu-pc-base/init-plugin';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import StepBasicConfig from 'liyu-pc-base/components/dbView/configSteps/StepBasicConfig.vue';
import StepDisplayConfig from 'liyu-pc-base/components/dbView/configSteps/StepDisplayConfig.vue';
import StepColumnConfig from 'liyu-pc-base/components/dbView/configSteps/StepColumnConfig.vue';
import StepMenu from 'liyu-pc-base/components/dbView/configSteps/StepMenu.vue';
import { STEP_FIELDS } from './configSteps/constants';
import { extractData } from './configSteps/helpers';
import { applyViewMeta, buildViewMeta } from './configSteps/viewMeta';

/* ★ v29.7：db_view 的「视图配置」新增/编辑弹窗组件。
   ★ v29.8：a-tabs 改 a-steps（用户定案），并新增「列配置」步骤（§3.1.4 字段配置子表）。
   ★ v29.23（用户定案批量调整）：① 「菜单与权限」独立成步（sort / parent_menu_flag /
     menu_project / roles 四项从基础配置移出）——步骤为 基础配置/菜单与权限/展示配置/列配置；
   ② 「探测列」按钮移除，列探测统一收敛到列配置步骤的「列探测刷新」（probe_columns，
     基于第 1 步表单的 SQL+数据源实时探测，不读写库），按 data_index 合并当前配置不清空；
   ③ 新建流程进入列配置步骤自动探测推断列；
   ④ 预览恒空修复（响应键 data→rows，见 runPreview 注释）；
   ⑤ **保存统一走 save_view**：主表字段 + columns + deleted_data_indexes 一次提交，
     事务内两表同落（新建/编辑共用），不再 create_db_view/update_db_view + save_columns
     两段式；「刷新」「保存列配置」按钮随之移除。
   view_meta 不再是裸 JSON 文本框，按「实际可配置的键」拆步骤——
   ★ v29.13：父菜单 RemoteTreeSelectPro（valueKey=state）/ menu_project / order→sort /
     org_filter 废除；★ v29.18：order_json 迁列配置默认排序、max_page_size /
     pivot_table_max_records 废除。
   未在表单展开的其余 view_meta 键（btn_list、advanced_base_conditions 等）
   编辑时原样保留（preservedMeta 合并回提交值），不会丢失。

   ★ v29.35（用户定案）：**按步骤拆组件**——本文件只留「外壳」：弹窗 + 步骤条 + a-form 容器
   + footer + 共享 model + 详情加载 / 提交编排。四个步骤落到 `dbView/configSteps/`：
     · StepBasicConfig.vue   基础配置（名称 / flag / 数据源 / SQL + 预览）
     · StepDisplayConfig.vue 展示配置（view_meta 白名单 5 键）
     · StepColumnConfig.vue  列配置（s-table 自适应高度 + 探测 + 两个行内模态框）
     · StepMenu.vue          菜单（父级路由 parent_menu_id / 隶属项目 menu_project）
   外加三个纯逻辑文件：`constants.ts`（数据字典）、`helpers.ts`（extractData/toList）、
   `viewMeta.ts`（view_meta 拆包与合并，纯函数）。

   ★ v29.45（用户定案，菜单归属重构）：步骤**顺序调整 + 裁剪** ——
     0 基础配置 / 1 展示配置 / 2 列配置 / **3 菜单**（原第 2 步「菜单与权限」挪到末位并改名）；
     该步裁掉「菜单索引(sort)」与「可见角色(roles)」，只留「父级路由 + 隶属项目」；
     `DbView` 的 `parent_menu_flag` / `menu_project` / `sort` / `roles` 四个字段**已删除**，
     菜单信息改由 `save_view` 同步到主服务的 `liyu_permission_menu`（详见
     `docs/design/07-menu-integration.md`）。连带把「父级路由」的取值从 **Menu.state**
     改成 **Menu.id**（`model.parent_menu_id`，直接写 `Menu.parent_id`）。

   拆分后的三条约定（改这些地方请一起看）：
   ① **model 仍是唯一数据源**，由外壳持有、按对象引用传给各步骤（模板里照旧 `model.xxx`）；
      外壳整体重建对象（initForEdit 的 `model.value = {...}`）时，各步骤的 `computed(() => props.model)`
      会跟着换到新对象（持有裸引用会失联）。
   ② **校验仍归外壳**：`rules` 就近定义在 StepBasicConfig（那 4 个字段只属于它），
      但 `a-form` / `formRef.validate*` / `STEP_FIELDS` 都在外壳 —— a-form-item 在外壳的
      `<a-form>` 组件树内，provide/inject 照常生效。
   ③ **跨步骤的两个口子**：`sqlError`（第 1 步渲染、第 3 步探测前清）仍由外壳持有，
      第 3 步用 `probeStart` 事件通知；列配置的提交载荷（columns / deleted_data_indexes）
      由 StepColumnConfig 用 `defineExpose` 提供，外壳通过 `colStepRef` 取。 */

interface Props {
  title: string;
  record: any;
}
const props = defineProps<Props>();
const emit = defineEmits(['cancel', 'ok']);

const formRef = ref<any>(null);
const loading = ref(false);
const open = ref(true);
/* ★ v29.35：改为 setup 顶层初始化（原先在 onMounted 里才赋值）——四个步骤组件都会在自己的
   onMounted 里读 formType 决定加载行为，而子组件的 mounted 早于父组件的 mounted，
   顶层初始化才能保证它们读到的一定是正确值。差异仅「挂载前就是正确的值」，无其他影响；
   onMounted 里那句同步赋值保留，兼容 record 被替换的情形。 */
const form_type = ref(props.record?.form_type || 'create');
const currentStep = ref(0);
const userStore = useUserStore();
const current_org = userStore.currentOrg;

/* 列配置步的进入标志：既控制那层 div 的显隐，也作为 `active` 传给 StepColumnConfig
   （它要在本步真正显示出来之后才挂 s-table 并自动探测，见该组件 watch(active) 的说明）。
   ★ v29.45：本步由第 4 步上移到**第 3 步**（菜单步挪到末位）→ 判据 3 改 2。
   ⚠️ 模板里那层 `v-show="colActive"` 与本行是**同一个来源**，换位只改这一处；
      但 `constants.ts` 的 `STEP_FIELDS` 是按步骤**位置**排的数组，注释必须同步。 */
const colActive = computed(() => currentStep.value === 2);

/* ★ v29.35：第 1 步的预览错误由外壳持有——第 3 步「列探测刷新」开始前也要清它
   （原先两处是同一作用域的自由变量，拆开后第 3 步改为 emit('probeStart') 通知） */
const sqlError = ref('');
const onProbeStart = () => {
  sqlError.value = '';
};

/* ★ v29.35：第 3 步的提交取值口（columns / deleted_data_indexes 都下沉到子组件了） */
const colStepRef = ref<any>(null);

/* ★ v29.35：这份 model 是四个步骤**共享**的表单载体（对象引用传给各步骤，它们直接改属性）。
   字段按步骤分组：基础配置（name/flag/data_source_id/sql）、展示配置（default_view_type/
   enable_view_types/is_pagination/pageSize/page_size_options）、菜单（parent_menu_id/menu_project）。
   ★ v29.45：删 `sort` / `role_ids`（字段已从 DbView 删除）；加 `parent_menu_id`
   （父级菜单的 **Menu.id**，替换原 `parent_menu_flag` 的 state 语义）。 */
const model = ref<any>({
  name: undefined,
  flag: undefined,
  data_source_id: undefined,
  parent_menu_id: undefined,
  menu_project: undefined,
  sql: undefined,
  // view_meta 结构化键（★ v29.18：order_json/max_page_size/pivot_table_max_records 废除——
  // order_json 迁到列配置 DbViewColumn 默认排序三字段，后两个机制删除）
  default_view_type: undefined,
  enable_view_types: [],
  is_pagination: true,
  pageSize: undefined,
  page_size_options: [],
});

/* ═══ 步骤导航：前进先校验当前步骤，后退/原地自由 ═══
   ★ v29.14（用户定案）：**编辑态**允许点步骤条直达任意步骤——前提是当前步骤
   校验通过（不通过则停留，错误由表单项显示）；最终保存时 handleOk 仍会全量校验。
   新建态维持「只许回退」。
   ★ v29.18：步骤收敛为 3 步；★ v29.23：菜单四项拆出 → 4 步
   （基础配置 / 菜单与权限 / 展示配置 / 列配置）。
   ★ v29.45（用户定案）：「菜单与权限」**挪到末位**并改名「菜单」→ 现为
   （基础配置 / 展示配置 / 列配置 / 菜单），仍 4 步。
   ⚠️ footer 的 `currentStep < 3` 与「新建态须走到最后一步」的判据**都不用改**（仍是 4 步、末位仍是 3）。
   ★ v29.35：进入列配置步骤时「若无列则自动探测」已下沉到 StepColumnConfig 的 watch(active)，
   这里不再关心列数据。 */
const stepFields = STEP_FIELDS;
const goNext = async () => {
  const fields = stepFields[currentStep.value] || [];
  if (fields.length) {
    try {
      await formRef.value?.validateFields(fields);
    } catch (e) {
      return; // 校验失败：停留当前步骤，错误提示已由表单项显示
    }
  }
  if (currentStep.value < 3) {
    currentStep.value += 1;
  }
};
const onStepChange = async (target: number) => {
  if (target <= currentStep.value) {
    currentStep.value = target; // 后退/原地自由
    return;
  }
  if (form_type.value !== 'edit') return; // 新建态：不可跳步前进
  const fields = stepFields[currentStep.value] || [];
  if (fields.length) {
    try {
      await formRef.value?.validateFields(fields);
    } catch (e) {
      return; // 校验失败：停留当前步骤
    }
  }
  currentStep.value = target;
};

/* ═══ view_meta：保留键由外壳持有 ═══
   ★ v29.35：拆步骤后，view_meta 的「拆包/合并」提成纯函数放 `./configSteps/viewMeta.ts`
   （它横跨外壳与「展示配置」步，放在任一侧都会造成反向依赖）。
   这里只留外壳需要的那一半：表单不消费的键。 */
let preservedMeta: Record<string, any> = {};

/* ═══ 详情 / 提交 ═══ */
const loadDetail = async (id: any) => {
  const res = await getApiData(
    { db_view_id: id, org_id: current_org?.id },
    `${props.record.model_app}/get_db_view`,
  );
  return extractData(res) || {};
};

const buildPayload = () => {
  const m = model.value;
  return {
    name: m.name,
    flag: m.flag,
    data_source_id: m.data_source_id,
    // ★ v29.45（用户定案）：`parent_menu_flag`(旧字段已删) → `parent_menu_id`（Menu.id）。
    //   null = 顶级菜单。后端据此直接写 `Menu.parent_id`，不再做 state→id 解析。
    parent_menu_id: m.parent_menu_id || null,
    menu_project: m.menu_project || null, // ★ v29.13：菜单隶属项目（区分模块）→ 写到菜单行
    sql: m.sql,
    view_meta: buildViewMeta(m, preservedMeta),
  };
};

const handleOk = async () => {
  try {
    /* ★ v29.17：编辑态「确定」任意步骤可点——只校验当前步骤的表单项（其余步骤的
       错误不可见，全量校验会让用户困惑）；新建态仍须走到最后一步，维持全量校验兜底。 */
    if (form_type.value === 'edit') {
      const fields = stepFields[currentStep.value] || [];
      if (fields.length) await formRef.value?.validateFields(fields);
    } else {
      await formRef.value.validate();
    }
    /* ★ v29.23（用户定案）：视图主表与列配置【同时保存】——统一走 save_view
       （事务内两表同落，新建/编辑共用），不再分 create_db_view + save_columns 两次请求。
       ★ v29.35：columns / deleted_data_indexes 改由 StepColumnConfig 提供（ref 调 expose 的方法）。 */
    const payload: any = {
      ...buildPayload(),
      columns: colStepRef.value?.buildColumnsPayload?.() ?? [],
      deleted_data_indexes: colStepRef.value?.getDeletedIndexes?.() ?? [],
    };
    if (form_type.value === 'edit') {
      payload.db_view_id = props.record.id;
    }
    loading.value = true;
    await getApiData(
      { ...payload, org_id: current_org?.id },
      `${props.record.model_app}/save_view`,
    );
    message.success('操作成功');
    colStepRef.value?.resetDeletedIndexes?.();
    event_obj()?.emit('refresh');
    emit('ok');
  } catch (error) {
    // 校验失败或接口异常：保持弹窗打开
  } finally {
    loading.value = false;
  }
};
const handleCancel = () => emit('cancel');

/* ★ v29.45（用户定案）：菜单两项**不再来自 DbView**（`parent_menu_flag` / `menu_project`
   两个字段已从模型删除），改从「该视图对应的菜单行」读 —— `/db_view/get_db_view_menu`。
   ⚠️ 必须在 `loadDetail()` 与下面那次整体赋值**之后**再回填：`initForEdit` 会用
      `model.value = {...}` 重建对象，顺序反了这两项会被覆盖掉。
   ⚠️ 走 `skip_error: true` + try/catch：菜单回显失败**不该阻塞编辑**（留空即可，
      保存时后端会按新值写/建菜单）。 */
const loadMenuInfo = async (flag: any) => {
  if (!flag) return;
  try {
    const res = await getApiData(
      { org_id: current_org?.id, flag },
      `${props.record.model_app}/get_db_view_menu`,
      false,
      { skip_error: true },
    );
    const data = extractData(res) || {};
    if (!data.exists) return; // 视图还没保存过菜单 / 菜单已被删除 → 两项留空
    model.value.parent_menu_id = data.parent_id ?? undefined;
    model.value.menu_project = data.menu_project || undefined;
  } catch (e) {
    // 保持留空
  }
};

const initForEdit = async () => {
  const id = props.record?.id;
  const record = (id != null ? await loadDetail(id) : props.record) || {};
  model.value = {
    ...model.value,
    name: record.name || undefined,
    flag: record.flag || undefined,
    data_source_id: record.data_source_id ?? undefined,
    sql: record.sql || undefined,
  };
  // ★ v29.35：拆包结果写回共享 model，其余键留在 preservedMeta（提交时原样合并）
  preservedMeta = applyViewMeta(model.value, record.view_meta || {});
  // ★ v29.45：菜单两项（父级 / 隶属项目）最后回填（必须在上面两次赋值之后）
  await loadMenuInfo(model.value.flag);
};

onMounted(async () => {
  if (props.record?.form_type) form_type.value = props.record.form_type;
  /* ★ v29.35：数据源下拉 / 角色列表 / 列配置预取 / 新建自动生成 flag 都已随各自步骤下沉，
     各自的 mounted 里自行加载；外壳只剩「编辑态拉详情」这一步。 */
  if (form_type.value === 'edit') {
    await initForEdit();
  }
});
</script>

<style scoped lang="less">
.config-steps {
  padding: 12px 0 4px;
}
/* ★ v29.17.2：步骤内容区固定高度（每步内部滚动），切换步骤时弹窗总高不跳变 */
.step-pane {
  height: calc(80vh - 150px);
  min-height: 360px;
  overflow-y: auto;
  overflow-x: hidden; /* ★ gutter 负 margin / 表格 min-width 等横向溢出不外溢 */
  padding-right: 4px;
}
/* ★ v29.34：列配置步专用——工具条固定在上、表格吃满剩余高度，**整个面板只留 s-table 一根滚动条**。
   ① 必须把基础 `.step-pane` 的 `overflow-y: auto` 收掉：否则出现的是「外层滚动条」，
      表头会跟着内容滚走，`flex: 1` 也就没有「剩余高度」可解析，自动高度链路从这一环断。
      纵向交给 s-table 表体、横向交给 `scroll.x`。
   ② 选择器刻意写成**复合** `.step-pane.step-pane--col`（0,2,0）而不是 `.step-pane--col`（0,1,0）：
      后者与 `.step-pane` 特异度打平，`overflow` 能否压住 `overflow-y: auto` 就**只取决于两个样式块的
      先后顺序**——将来有人调整顺序就会静默退回 auto（多出一根滚动条、表格高度失去意义）。
      与抽屉那次 `.csc-drawer.ant-drawer .ant-drawer-body` 是同一类坑，写成复合/三段式即与顺序无关。
   ★ v29.35：本 div 的直接子元素就是 StepColumnConfig 内部的 `.col-toolbar` 与
      `.col-table-wrapper`（fragment 组件不产生额外 DOM），故 `flex: 1` 的参照仍是它。 */
.step-pane.step-pane--col {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
</style>
